import React, { useState, useCallback } from 'react';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
	CFormInput, CFormSelect, CBadge,
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilPlus, cilSearch, cilPencil, cilTrash } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import Select from 'react-select';
import { useSupportCategories, useInactiveSupportCategories } from 'src/hooks/useSupportCategories';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const urlApi = '/faq';
const urlRouter = '/faq';
const itemsPerPage = 30;

export async function LoaderFaq() {
	return AxiosInstance.get(urlApi, { params: { page: 1, itemsPerPage } });
}

const Faq = () => {
	const loaderData = useLoaderData();
	const navigate = useNavigate();
	const activeCategories = useSupportCategories();
	const inactiveCategories = useInactiveSupportCategories();

	const [rows, setRows] = useState(loaderData?.data?.data ?? []);
	const [rowCountState, setRowCountState] = useState(loaderData?.data?.total ?? 0);
	const [searchText, setSearchText] = useState('');
	const [isActiveFilter, setIsActiveFilter] = useState('');
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [showLoader, setShowLoader] = useState(false);
	const [page, setPage] = useState(0);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const handleDelete = (row) => {
		confirmAlert({
			title: 'Ви впевнені що хочете видалити цей запис?',
			message: <></>,
			buttons: [
				{
					label: 'Так',
					onClick: () => {
						// console.log('видалення', row.id)
						AxiosInstance.delete(`${urlApi}/${row.id}`).then(() => {
							getRowsTable();
							toast.success('Видалено');
						}).catch(() => toast.error('Помилка видалення'));

					},
				},
				{
					label: 'Ні',
					onClick: () => { },
				},
			],
		});
	};

	const getRowsTable = (p = 1, search = searchText, isActive = isActiveFilter, cats = selectedCategories) => {
		setShowLoader(true);
		const params = { page: p, itemsPerPage };
		if (search) params.search = search;
		if (isActive !== '') params.is_active = isActive === 'true';
		if (cats.length > 0) params.category_ids = cats.map(c => c.value);
		AxiosInstance.get(urlApi, { params }).then(response => {
			setRows(response.data.data ?? []);
			setRowCountState(response.data.total ?? 0);
			setShowLoader(false);
		}).catch(() => setShowLoader(false));
	};

	const handleSearch = () => {
		setPage(0);
		getRowsTable(1);
	};

	const handleClearFilters = () => {
		setSearchText('');
		setIsActiveFilter('');
		setSelectedCategories([]);
		setPage(0);
		getRowsTable(1, '', '', []);
	};

	const handlePageChange = (newPage) => {
		setPage(newPage);
		getRowsTable(newPage + 1);
	};

	const categoryOptions = [{ value: -1, label: 'Без категорії' }, ...activeCategories.map(c => ({ value: c.id, label: c.name }))];

	const columns = [
		{ field: 'id', headerName: 'ID', width: 50 },
		{
			field: 'question', headerName: 'Питання', width: 350,
			renderCell: (params) => (
				<Link to={`${urlRouter}/view/${params.row.id}`} className="table-name-link" title={params.row.question}>
					{params.row.question?.length > 60 ? params.row.question.substring(0, 60) + '…' : params.row.question}
				</Link>
			),
		},
		{
			field: 'sort_order', headerName: 'Порядок', width: 100,
			renderCell: (params) => params.row.sort_order ?? 0,
		},
		{
			field: 'categories', headerName: 'Категорії', width: 200, sortable: false,
			renderCell: (params) => {
				const cats = params.row.categories || [];
				if (!cats.length) return <span>—</span>;
				const parts = cats.map(cat => {
					const active = activeCategories.find(c => c.id === cat.id);
					if (active) return { name: cat.name, inactive: false };
					const inactive = inactiveCategories.find(c => c.id === cat.id);
					return { name: inactive?.name || cat.name, inactive: true };
				});
				return (
					<span title={parts.map(p => p.name).join(', ')}>
						{parts.map((p, i) => (
							<React.Fragment key={i}>
								{i > 0 && ', '}
								{p.inactive
									? <span style={{ textDecoration: 'line-through', color: '#999' }}>{p.name}</span>
									: p.name
								}
							</React.Fragment>
						))}
					</span>
				);
			},
		},
		{
			field: 'anchor', headerName: 'Anchor', width: 120,
			renderCell: (params) => params.row.anchor || '—',
		},
		{
			field: 'is_active', headerName: 'Активна', width: 80,
			renderCell: (params) => (
				<CBadge color={params.row.is_active ? 'success' : 'secondary'}>
					{params.row.is_active ? 'Активна' : 'Неактивна'}
				</CBadge>
			),
		},
		{
			field: 'action', headerName: 'Переглянути', headerAlign: 'center', align: 'center', sortable: false, width: 105,
			renderCell: (params) => (
				<Link to={`${urlRouter}/view/${params.row.id}`} className='custom-view-icon' title="Переглянути запис">
					<CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
		{
			field: 'action_edit', headerName: 'Редагувати', headerAlign: 'center', align: 'center', sortable: false, width: 95,
			renderCell: (params) => (
				<Link to={`${urlRouter}/update/${params.row.id}`} className='custom-edit-icon' title="Редагувати запис">
					<CIcon icon={cilPencil} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
		// {
		// 	field: 'action_delete', headerName: 'Видалити', headerAlign: 'center', align: 'center', sortable: false, width: 85,
		// 	renderCell: (params) => (
		// 		<CButton color="danger" variant="outline" size="sm" onClick={() => handleDelete(params.row)} title="Видалити запис">
		// 			<CIcon icon={cilTrash} customClassName="nav-icon" height={18} />
		// 		</CButton>
		// 	),
		// },
	];

	return (
		<CRow>
			<ToastContainer />
			<CCol xs={12}>
				<CCard className="mb-4">
					<CAccordion flush>
						<CAccordionItem>
							<CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
							<CAccordionBody>
								<CRow className="my-3 mx-2">
									<CCol md={3} className="filter-field">
										<CFormInput
											placeholder="Пошук по тексту"
											value={searchText}
											onChange={(e) => setSearchText(e.target.value)}
											onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={3} className="filter-field">
										<Select
											isMulti
											options={categoryOptions}
											value={selectedCategories}
											onChange={setSelectedCategories}
											placeholder="Категорії..."
											isClearable
											classNamePrefix="react-select"
										/>
									</CCol>
									<CCol md={3} className="filter-field">
										<CFormSelect
											value={isActiveFilter}
											onChange={(e) => setIsActiveFilter(e.target.value)}
										>
											<option value="">Всі (активні і неактивні)</option>
											<option value="true">Тільки активні</option>
											<option value="false">Тільки неактивні</option>
										</CFormSelect>
									</CCol>
									<CCol md={3} className="filter-field" style={{ textAlign: 'end' }}>
										<CButton color="primary" className="me-2" onClick={handleSearch} title="Застосувати фільтри">
											Шукати
										</CButton>
										<CButton color="secondary" variant="outline" onClick={handleClearFilters} title="Очистити всі фільтри">
											Очистити фільтри
										</CButton>
									</CCol>
								</CRow>
							</CAccordionBody>
						</CAccordionItem>
					</CAccordion>
					<CCardHeader><strong>FAQ</strong></CCardHeader>
					<CCardBody>
						<p className="text-medium-emphasis small">
							<Link to={`${urlRouter}/create`}>
								<CButton color="secondary" title="Створити новий FAQ-запис">
									<CIcon icon={cilPlus} customClassName="nav-icon" height={15} />Створити
								</CButton>
							</Link>
						</p>
						<div style={{ height: 600, width: '100%' }}>
							<DataGrid
								rows={rows}
								columns={columns}
								page={page}
								pageSize={itemsPerPage}
								rowsPerPageOptions={[itemsPerPage]}
								pagination
								paginationMode="server"
								rowCount={rowCountState}
								loading={showLoader}
								onPageChange={handlePageChange}
								initialState={{
									sorting: { sortModel: [{ field: 'sort_order', sort: 'asc' }] },
								}}
							/>
						</div>
						<div className="d-flex justify-content-start mt-3">
							<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
								Повернутись
							</CButton>
						</div>
					</CCardBody>
				</CCard>
			</CCol>
		</CRow>
	);
};

export default Faq;
